import { useEffect, useMemo, useState } from "react";

/**
 * Manual completo de la plataforma.
 * Página estática (todo el contenido vive aquí; no requiere backend).
 * Pensada para revisar en cualquier momento. Diseño escritorio + móvil.
 */
const SECTIONS = [
  { id: "intro", label: "1. Introducción" },
  { id: "acceso", label: "2. Acceso y seguridad" },
  { id: "proyectos", label: "3. Proyectos" },
  { id: "ideas", label: "4. Ideas y notas de voz" },
  { id: "dossier", label: "5. Dossier" },
  { id: "guion", label: "6. Guion" },
  { id: "calendario", label: "7. Calendario de producción" },
  { id: "presupuesto", label: "8. Presupuesto ICAA" },
  { id: "financiacion", label: "9. Plan de financiación" },
  { id: "biblioteca", label: "10. Biblioteca de referencias" },
  { id: "exportacion", label: "11. Exportación" },
  { id: "ia", label: "12. Resumen de capacidades IA" },
  { id: "atajos", label: "13. Buenas prácticas y atajos" },
  { id: "guia-presupuesto", label: "14. Guía pro: presupuesto y calendario" },
  { id: "faq", label: "15. Preguntas frecuentes" },
];

export default function ManualPage() {
  const [active, setActive] = useState("intro");

  // scroll-spy basic
  useEffect(() => {
    const ids = SECTIONS.map((s) => s.id);
    const onScroll = () => {
      for (const id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;
        const r = el.getBoundingClientRect();
        if (r.top > 80 && r.top < 240) { setActive(id); return; }
      }
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goto = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - 80;
    window.scrollTo({ top, behavior: "smooth" });
  };

  const updated = useMemo(() => new Date("2026-06-03").toLocaleDateString("es-ES", {
    day: "2-digit", month: "long", year: "numeric",
  }), []);

  return (
    <div className="min-h-screen bg-gb-bg font-serif text-stone-900" data-testid="manual-page">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-8 sm:py-12">
        {/* Hero */}
        <div className="mb-8 border-b border-stone-300/70 pb-8 sm:mb-12 sm:pb-12">
          <p className="text-[10px] font-semibold uppercase tracking-[0.45em] text-[#26775E]">grabarte · audiovisual studio</p>
          <h1 className="mt-3 font-display text-3xl leading-[1.05] tracking-tight sm:text-5xl">Manual de uso</h1>
          <p className="mt-3 max-w-2xl text-sm text-stone-600 sm:text-base">
            Guía detallada de todo lo que puedes hacer dentro de la plataforma — desde crear un proyecto
            hasta exportar el dossier listo para entregar a un organismo público.
          </p>
          <p className="mt-2 text-xs text-stone-500">Última actualización: {updated}</p>
        </div>

        <div className="grid gap-8 lg:grid-cols-[240px,1fr] lg:gap-12">
          {/* TOC */}
          <aside className="hidden lg:block">
            <nav className="sticky top-24 space-y-1 text-sm" data-testid="manual-toc">
              {SECTIONS.map((s) => (
                <button
                  key={s.id}
                  onClick={() => goto(s.id)}
                  data-testid={`toc-${s.id}`}
                  className={`block w-full rounded px-3 py-1.5 text-left transition ${
                    active === s.id
                      ? "bg-stone-900 text-[#FAFAF6]"
                      : "text-stone-600 hover:bg-stone-200/60 hover:text-stone-900"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </nav>
          </aside>

          {/* TOC mobile (select) */}
          <div className="lg:hidden">
            <select
              value={active}
              onChange={(e) => goto(e.target.value)}
              data-testid="manual-toc-mobile"
              className="w-full rounded-md border border-stone-300 bg-white p-3 text-sm"
            >
              {SECTIONS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>

          {/* Content */}
          <article className="prose prose-stone max-w-none">
            <Section id="intro" title="1. Introducción">
              <p>
                <strong>Grabarte · Audiovisual Studio</strong> es una plataforma cerrada para el equipo
                de Grabarte 360. Sirve para crear, presupuestar y producir proyectos audiovisuales
                (largos documentales y series), reuniendo en un solo lugar:
              </p>
              <ul>
                <li><strong>Dossier editorial</strong> (sinopsis, contexto, personajes, referencias, biofilmografía, cartas de intención).</li>
                <li><strong>Guion</strong> en tres formatos: tratamiento literario con secuencias, escaleta y guion técnico.</li>
                <li><strong>Calendario de producción</strong> por bloques.</li>
                <li><strong>Presupuesto ICAA</strong> en 12 capítulos con validación automática (caps 11 y 12).</li>
                <li><strong>Plan de financiación</strong> público / privado / internacional / propio.</li>
                <li><strong>Biblioteca de referencias</strong> (PDFs OCR-eados) usada como guía de voz por la IA.</li>
                <li><strong>Ideas y notas de voz</strong> transcribibles con Whisper.</li>
                <li><strong>Asistente IA</strong> integrado (Claude para texto, Gemini Nano Banana para imagen) para
                  generar, reescribir y dialogar sobre cualquier sección.</li>
                <li><strong>Exportación</strong> a PowerPoint, PDF y Excel.</li>
              </ul>
              <p>
                Todo el contenido se guarda automáticamente. Puedes trabajar varios proyectos en paralelo
                y compartirlos entre los miembros del equipo autorizados.
              </p>
            </Section>

            <Section id="acceso" title="2. Acceso y seguridad">
              <p>
                La plataforma está cerrada al equipo autorizado. Para entrar:
              </p>
              <ol>
                <li>Abre la URL pública.</li>
                <li>Pulsa <strong>Iniciar sesión con Google</strong>.</li>
                <li>Elige tu cuenta de Grabarte 360 (la lista blanca actual contiene
                  <em> manu@grabarte360.com</em> y <em>eduardo@grabarte360.com</em>).</li>
                <li>Si tu cuenta no está autorizada, recibirás un aviso y no podrás entrar.</li>
              </ol>
              <p>
                La sesión se conserva durante 7 días en una cookie segura. Para cerrar sesión, despliega
                el menú de tu avatar en la esquina superior derecha del header y pulsa <em>Cerrar sesión</em>.
              </p>
              <Tip>
                ¿Necesitas dar acceso a otra persona del equipo? Pídelo y se añade su correo a la lista blanca.
                Cualquier intento de acceder con una cuenta no autorizada queda bloqueado en backend.
              </Tip>
            </Section>

            <Section id="proyectos" title="3. Proyectos">
              <h3>3.1 Pantalla principal</h3>
              <p>
                Al iniciar sesión llegas a <strong>Proyectos</strong>. Verás un panel con:
              </p>
              <ul>
                <li>Total de proyectos.</li>
                <li>Presupuesto agregado de todos.</li>
                <li>Financiación captada agregada.</li>
                <li>Tipologías (largometraje, serie, etc.).</li>
                <li>Tabla / tarjetas de tus proyectos con director/a, presupuesto y % cubierto.</li>
              </ul>

              <h3>3.2 Crear un proyecto</h3>
              <ol>
                <li>Pulsa <strong>+ Nuevo proyecto</strong> (esquina superior derecha o en la home).</li>
                <li>Rellena título, tipo (largometraje, serie, etc.), nº episodios (si aplica), director/a y productora.</li>
                <li>Director y Productora tienen <strong>autocompletado</strong> a partir de proyectos previos.</li>
                <li>Pulsa <strong>Crear</strong>. Se inicializa con un dossier vacío, los 12 capítulos del presupuesto ICAA y la estructura básica.</li>
              </ol>

              <h3>3.3 Borrar un proyecto</h3>
              <p>
                Desde la lista, el botón <em>×</em> de la fila del proyecto. Se pide confirmación. La acción
                es definitiva: se borran dossier, guion, calendario y presupuesto asociados.
              </p>

              <h3>3.4 Datos del proyecto</h3>
              <p>
                Dentro de cada proyecto encontrarás un desplegable <em>Datos del proyecto / productora</em> con:
              </p>
              <ul>
                <li>Productora (empresa) y CIF/NIF.</li>
                <li><strong>Director/a</strong>.</li>
                <li><strong>Productor/a</strong> (persona física, distinto de la productora).</li>
                <li><strong>Guionistas</strong> (lista de nombres, separados por comas).</li>
                <li>Tipo de proyecto y nº de capítulos.</li>
                <li>Localizaciones principales.</li>
              </ul>
              <p>
                Estos campos aparecen en la portada del dossier y en los créditos finales del PDF/PPT.
              </p>
            </Section>

            <Section id="ideas" title="4. Ideas y notas de voz">
              <p>
                Primera pestaña dentro de cada proyecto. Pensada para capturar ideas rápidas — desde el
                móvil o el ordenador — mientras trabajas.
              </p>
              <h3>4.1 Notas de texto</h3>
              <ol>
                <li>Escribe en el cuadro de arriba.</li>
                <li>Pulsa <strong>Guardar nota</strong>.</li>
                <li>La nota aparece arriba en la lista, con fecha y hora.</li>
                <li>Puedes editar el texto en cualquier momento (se guarda al perder el foco).</li>
              </ol>

              <h3>4.2 Notas de voz con Whisper</h3>
              <ol>
                <li>Pulsa <strong>Grabar voz</strong>. La primera vez el navegador pedirá permisos de micrófono.</li>
                <li>Habla. Verás el contador de tiempo.</li>
                <li>Pulsa <strong>Detener</strong>.</li>
                <li>El audio se sube al servidor y se transcribe con <strong>OpenAI Whisper</strong> en español. Tarda
                  unos 3-8 segundos dependiendo del audio (hasta 25 MB por nota).</li>
                <li>La nota aparece con el <em>reproductor de audio</em> + la <em>transcripción editable</em>.</li>
              </ol>
              <Tip>
                Ideal en rodaje, viajes, paseos. Habla con naturalidad: Whisper entiende menorquín, catalán,
                español y mezcla. La transcripción es editable, así que puedes pulirla luego.
              </Tip>
            </Section>

            <Section id="dossier" title="5. Dossier">
              <p>
                El dossier es la pieza central. Está dividido en secciones editables que después se exportan
                como documento editorial paginado (PPT / PDF).
              </p>

              <h3>5.1 Estructura de secciones</h3>
              <ul>
                <li><strong>Título</strong> — Nombre del proyecto (puede cambiar durante el desarrollo).</li>
                <li><strong>Portada</strong> — Imagen + 1-3 líneas que capten el tono.</li>
                <li><strong>Logline</strong> — 1-2 frases con protagonista + conflicto + apuesta dramática.</li>
                <li><strong>Sinopsis</strong> — 3-5 párrafos: qué, quién, dónde, cuándo.</li>
                <li><strong>Nota de intenciones</strong> — Por qué cuento esta historia, en primera persona.</li>
                <li><strong>Vínculo</strong> — ¿Por qué yo cuento esto?</li>
                <li><strong>Contexto</strong> — Marco social, geográfico, histórico, con datos concretos.</li>
                <li><strong>Personajes</strong> — Fichas múltiples (estructurado).</li>
                <li><strong>Tratamiento</strong> — El cómo audiovisual: cámara, luz, sonido, ritmo.</li>
                <li><strong>Referencias</strong> — Obras audiovisuales que dialogan con el proyecto (estructurado).</li>
                <li><strong>Biofilmografía</strong> — Fichas del equipo: director/a, productor/a, DOP, montaje, productora… (estructurado).</li>
                <li><strong>Cartas de intención</strong> — Cartas de coproducción / apoyo institucional (estructurado).</li>
                <li><strong>Producción</strong> — Enlace al presupuesto y al plan de financiación.</li>
              </ul>

              <h3>5.2 Botones IA por sección</h3>
              <p>En secciones de texto clásico (sinopsis, contexto, tratamiento…) verás:</p>
              <ul>
                <li><strong>✨ Generar</strong> — Claude redacta una primera versión desde cero usando el contexto del
                  proyecto y, opcionalmente, las referencias que hayas marcado.</li>
                <li><strong>💬 Mejorar con IA</strong> — Abre un panel de chat lateral. Le explicas qué quieres cambiar
                  ("hazlo más conciso", "tono más literario", "amplía la descripción visual"). La IA devuelve una
                  versión completa con dos botones: <em>Reemplazar</em> (cambia el texto y cierra el panel) o
                  <em>Añadir al final</em> (apila la versión nueva).</li>
                <li><strong>+ Imagen</strong> — Abre el <em>Studio de imagen</em>. Genera con Nano Banana (Gemini) o sube
                  imagen propia. Se añaden al carrusel de la sección.</li>
              </ul>

              <h3>5.3 Secciones estructuradas (fichas)</h3>
              <p>
                Personajes, referencias, biofilmografía y cartas funcionan con <strong>tarjetas</strong>:
              </p>
              <ol>
                <li>Pulsa <strong>✨ Añadir con Claude</strong> para que la IA proponga una ficha coherente con el dossier
                  (puedes darle un brief opcional: "carta de TV3 para coproducción catalán").</li>
                <li>O pulsa <strong>+ En blanco</strong> y rellena tú.</li>
                <li>Cada tarjeta tiene título, meta (cargo / tipo) y cuerpo. En personajes, referencias y
                  biofilmografía además puedes asociar una imagen.</li>
              </ol>
              <Tip>
                En biofilmografía especificas el <strong>cargo</strong> (Director, Productor, DOP, Montaje, Productora…).
                Aparece como subtítulo en la slide del dossier exportado.
              </Tip>

              <h3>5.4 Imágenes</h3>
              <p>
                Cualquier sección no estructurada permite añadir múltiples imágenes (carrusel). Las imágenes se
                generan con Gemini (Nano Banana) a partir de un prompt o se suben desde tu dispositivo.
              </p>
            </Section>

            <Section id="guion" title="6. Guion">
              <p>El guion tiene tres formatos. Puedes alternar entre ellos en la barra superior:</p>

              <h3>6.1 Tratamiento literario</h3>
              <p>Estructura: <strong>Bloques → Secuencias</strong>.</p>
              <ul>
                <li>Cada bloque tiene título, periodo/fecha, sinopsis de una línea y cuerpo desarrollado.</li>
                <li>Dentro de cada bloque puedes añadir <strong>secuencias</strong> con título, localización, sinopsis y cuerpo.</li>
                <li>Reordena bloques y secuencias con las flechas <em>↑↓</em>.</li>
                <li>Elimina con <em>×</em>. Los números se reordenan automáticamente.</li>
              </ul>

              <h3>6.2 Escaleta</h3>
              <p>
                Tabla plana de escenas: nº, título / acción, localización, día/noche, minutos. Buena para tener
                una visión rápida de la duración.
              </p>

              <h3>6.3 Guion técnico</h3>
              <p>
                Escenas con cabecera (INT./EXT. lugar — día), acción y diálogos. Formato similar al estándar
                cinematográfico (no es Final Draft pero se exporta a PDF limpio).
              </p>

              <h3>6.4 Importar texto con IA</h3>
              <ol>
                <li>Pulsa <strong>Importar texto con IA</strong> arriba.</li>
                <li>Pega tu texto (hasta 60.000 caracteres; se procesa en paralelo en trozos de 4.500).</li>
                <li>Elige formato destino y modo (añadir al final o reemplazar).</li>
                <li>Pulsa <strong>Distribuir con IA</strong>. La IA detecta separadores naturales (capítulos, partes,
                  fechas, lugares) y construye la estructura.</li>
              </ol>
              <Tip>
                Si tienes un tratamiento en Word, copia y pega aquí: la IA lo descompone en bloques con secuencias
                respetando tu voz.
              </Tip>

              <h3>6.5 Derivar formatos</h3>
              <p>
                Cuando tienes el <strong>tratamiento literario</strong> con bloques y secuencias, aparece una barra verde
                con dos botones:
              </p>
              <ul>
                <li><strong>Generar escaleta</strong> — Convierte tu tratamiento en escaleta (escenas con duración estimada).</li>
                <li><strong>Generar guion técnico</strong> — Convierte tu tratamiento en escenas con INT./EXT., acción y diálogos sugeridos.</li>
              </ul>
              <p>
                Cada uno reemplaza el formato destino con escenas derivadas; el tratamiento original se conserva.
              </p>

              <h3>6.6 Chat IA por bloque / secuencia / escena</h3>
              <p>
                En la esquina superior derecha del cuerpo de cada bloque, secuencia y escena verás un mini-botón
                <strong> 💬 IA</strong>. Lo pulsas → se abre el chat lateral → pides el cambio → aplicas la propuesta.
              </p>

              <h3>6.7 Notas internas</h3>
              <p>
                Arriba del listado hay un cuadro de notas generales del guion (visibles solo para el equipo, no se exportan).
              </p>
            </Section>

            <Section id="calendario" title="7. Calendario de producción">
              <h3>7.1 Bloques de producción</h3>
              <p>
                El calendario se compone de <strong>bloques</strong>. Cada bloque tiene:
              </p>
              <ul>
                <li>Título.</li>
                <li>Fase: <em>desarrollo · preproducción · rodaje · postproducción · distribución</em> (con código de color).</li>
                <li>Fecha inicio / fin (opcional).</li>
                <li>Días estimados.</li>
                <li>Localizaciones / notas.</li>
              </ul>
              <p>
                Los bloques con fecha aparecen en la <strong>línea temporal</strong> con barras horizontales coloreadas
                por fase.
              </p>

              <h3>7.2 Plan de rodaje sugerido</h3>
              <p>
                Si ya tienes escenas en el guion (escaleta o técnico), arriba aparece una barra verde:
              </p>
              <ul>
                <li><strong>Sugerir bloques</strong> — Añade al final. La IA agrupa las escenas por
                  <em> localización + día/noche</em> y propone bloques con número estimado de jornadas. Las notas del
                  bloque incluyen las escenas que toca rodar.</li>
                <li><strong>Reemplazar</strong> — Sustituye los bloques actuales por la nueva propuesta.</li>
              </ul>
              <p>
                Las fechas concretas las pones tú; la IA solo organiza qué se rueda junto a qué.
              </p>

              <h3>7.3 Estadísticas</h3>
              <p>
                En la cabecera ves un panel con el número de bloques y días por cada fase. Total de jornadas de
                rodaje destacado.
              </p>
            </Section>

            <Section id="presupuesto" title="8. Presupuesto ICAA">
              <p>
                Estructura completa de los <strong>12 capítulos ICAA</strong>, con sub-capítulos y partidas. Cada
                partida tiene unidades, precio unitario y total automático.
              </p>

              <h3>8.1 Cómo trabajar</h3>
              <ol>
                <li>Despliega un capítulo pulsando sobre su cabecera.</li>
                <li>Edita unidades y precios unitarios; el total se recalcula al instante.</li>
                <li>Añade partidas extra con <strong>+ Añadir partida</strong> dentro del sub-capítulo correspondiente.</li>
                <li>Borra partidas con <em>×</em>.</li>
              </ol>

              <h3>8.2 Validación automática</h3>
              <p>
                La plataforma vigila dos límites legales del ICAA:
              </p>
              <ul>
                <li><strong>Capítulo 11</strong> (Imprevistos): no puede superar el <strong>7%</strong> del total.</li>
                <li><strong>Capítulo 12</strong> (Gastos generales): no puede superar el <strong>20%</strong> del total.</li>
              </ul>
              <p>
                Si alguno se excede, aparece un aviso ámbar en el panel superior y se marca el capítulo en rojo.
              </p>

              <h3>8.3 Auto-ajuste con IA</h3>
              <p>
                Si los caps 11 y/o 12 están fuera de rango, pulsa <strong>Ajustar con IA</strong>. La IA propone
                una redistribución mínima entre capítulos que cumple los límites. La ves en un modal y
                puedes <em>aceptar</em>, <em>rechazar</em> o <em>editar manualmente</em>.
              </p>
            </Section>

            <Section id="financiacion" title="9. Plan de financiación">
              <p>
                Tabla con fuentes de financiación. Cada fila tiene:
              </p>
              <ul>
                <li>Categoría (Pública, TV, Internacional, Privada, Producción propia).</li>
                <li>Nombre / detalle.</li>
                <li>Importe.</li>
                <li>Confirmado (sí/no).</li>
              </ul>
              <p>
                Al pie de la tabla se calcula el total comprometido y el % de cobertura sobre el presupuesto.
              </p>
              <Tip>
                Si todavía no tienes confirmaciones, deja la columna en <em>No</em>. Cuando llegue el contrato,
                cambias a <em>Sí</em>. Las cifras agregadas en la home reflejan ambos valores.
              </Tip>
            </Section>

            <Section id="biblioteca" title="10. Biblioteca de referencias">
              <p>
                Espacio para subir dossieres y guiones de proyectos anteriores. La IA los lee y aprende tu voz
                para usarla cuando genera o reescribe textos.
              </p>

              <h3>10.1 Subir referencias</h3>
              <ol>
                <li>Entra en <strong>Biblioteca</strong> (menú superior).</li>
                <li>Arrastra o sube un PDF, DOCX o PPTX (cualquier tamaño razonable, p. ej. dossier completo).</li>
                <li>Si el archivo es PDF escaneado, se aplica <strong>OCR</strong> automáticamente (puede tardar 30-90 s).</li>
                <li>La IA propone metadatos: título, género, duración, etiquetas. Aceptas o editas.</li>
              </ol>

              <h3>10.2 Cómo se usa</h3>
              <p>
                Al generar o reescribir secciones del dossier puedes marcar referencias concretas en el modal
                de IA. El sistema usa esos textos como guía de tono y estilo (RAG ligero).
              </p>

              <h3>10.3 Visor PDF interno</h3>
              <p>
                Al pulsar sobre una referencia, se abre un visor PDF dentro de la app. Recuerda en qué página
                te quedaste para retomar la lectura.
              </p>
            </Section>

            <Section id="exportacion" title="11. Exportación">
              <p>
                Cada proyecto se puede exportar en cuatro formatos. Los botones están en la cabecera del proyecto:
              </p>
              <ul>
                <li><strong>Presupuesto Excel</strong> — Hoja de cálculo con los 12 capítulos y desgloses.</li>
                <li><strong>Presupuesto PDF</strong> — Resumen editorial paginado del presupuesto.</li>
                <li><strong>Dossier PPT</strong> — Presentación editable, una idea por slide. Los textos largos se
                  paginan automáticamente en varias slides.</li>
                <li><strong>Dossier PDF</strong> — Versión imprimible del dossier, con portada, créditos
                  (Dirección · Guion · Producción · Productora) y secciones desplegadas.</li>
              </ul>
              <Tip>
                Si quieres ajustar el diseño final (tipografía, logos, colores) usa la versión PPT como base:
                cada slide es editable en PowerPoint o Keynote.
              </Tip>
            </Section>

            <Section id="ia" title="12. Resumen de capacidades IA">
              <p>La plataforma combina dos modelos accesibles a través de la <strong>Clave Universal Emergent</strong>:</p>
              <ul>
                <li><strong>Claude (Anthropic)</strong> para textos: generación, reescritura, chat de mejora, distribución de
                  guion, derivación de formatos, plan de rodaje sugerido, extracción de metadatos de PDFs.</li>
                <li><strong>Gemini Nano Banana (Google)</strong> para imágenes: portadas, retratos de personajes, frames
                  inspirados en referencias, imágenes de equipo y cartas.</li>
                <li><strong>OpenAI Whisper</strong> para transcripción de notas de voz.</li>
              </ul>

              <h3>12.1 Generar / Mejorar / Chat</h3>
              <p>
                Tres niveles distintos según lo que necesites:
              </p>
              <ul>
                <li><strong>Generar</strong> ▸ crea texto desde cero usando el contexto del dossier.</li>
                <li><strong>Reescribir</strong> ▸ propone una versión alternativa de un texto existente.</li>
                <li><strong>Mejorar con IA (chat)</strong> ▸ conversación iterativa: pides cambios concretos, la IA propone
                  versiones, tú aplicas o sigues iterando.</li>
              </ul>

              <h3>12.2 Saldo</h3>
              <p>
                Si la Clave Universal se queda sin saldo, verás un error en las llamadas IA. Para recargar,
                entra en tu perfil de Emergent → <em>Universal Key</em> → <em>Añadir saldo</em> o activa el top-up automático.
              </p>
            </Section>

            <Section id="atajos" title="13. Buenas prácticas y atajos">
              <ul>
                <li><strong>Guardado automático</strong>: cada vez que pierdes el foco de un campo, se guarda. No hace falta
                  pulsar "Guardar" manualmente, pero el botón existe arriba a la derecha como red de seguridad.</li>
                <li><strong>Cmd/Ctrl + Enter</strong> en el chat IA envía el mensaje.</li>
                <li>En móvil, los <strong>tabs</strong> dentro del proyecto se deslizan horizontalmente.</li>
                <li>Empieza siempre por la <strong>sinopsis</strong> y la <strong>nota de intenciones</strong>: dan contexto
                  a toda la IA posterior.</li>
                <li>Sube al menos 1-2 <strong>referencias</strong> reales antes de generar masivamente — la voz mejora mucho.</li>
                <li>Para tratamientos largos, importa el texto completo (hasta 60K chars) y deja que la IA lo
                  estructure por ti.</li>
                <li>Las notas de voz son ideales para capturar ideas en rodaje desde el móvil.</li>
              </ul>
            </Section>

            <Section id="guia-presupuesto" title="14. Guía profesional: presupuesto y calendario">
              <aside className="my-3 rounded-lg border border-[#941620]/40 bg-[#941620]/10 px-4 py-3 text-[13px] text-stone-700">
                <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#a96f15]">
                  Fuente
                </p>
                <p className="mt-1">
                  Esta sección está basada en el artículo <em>«A Fresh 2025 Introduction to Documentary Budgeting and Scheduling
                  (With New Templates!)»</em> de <strong>Robert Bahar</strong>, publicado en diciembre 2024 por la{" "}
                  <strong>International Documentary Association</strong>. Adaptado al contexto español y a la
                  estructura ICAA. <a className="text-[#26775E] underline" href="https://www.documentary.org/online-feature/2025-introduction-documentary-budgeting-and-scheduling" target="_blank" rel="noreferrer">Leer original ↗</a>
                </p>
              </aside>

              <h3>14.1 El triángulo de calidad</h3>
              <p>
                Existe un adagio profesional: <strong>«Rápido, barato o bueno — elige dos»</strong>. Aplicado al
                cine documental, los tres vértices son:
              </p>
              <ul>
                <li><strong>Concepto / visión artística</strong> (¿qué quieres contar y cómo?)</li>
                <li><strong>Calendario</strong> (¿cuánto tiempo tienes?)</li>
                <li><strong>Presupuesto</strong> (¿cuánto dinero tienes?)</li>
              </ul>
              <p>
                Cambiar uno de los vértices siempre afecta a los otros dos. Antes de presupuestar, define
                primero la visión. Sin visión clara no hay calendario; sin calendario no hay presupuesto.
              </p>

              <h3>14.2 «Finish line»: ¿hasta dónde llega tu presupuesto?</h3>
              <p>
                Antes de empezar a meter cifras debes saber qué cubre exactamente este presupuesto. Bahar
                propone tres escenarios habituales:
              </p>
              <ol>
                <li><strong>Encargo cerrado</strong> (TV, plataforma, cliente) → presupuestas hasta los entregables contractuales.</li>
                <li><strong>Independiente con búsqueda de distribución</strong> → presupuestas hasta el estreno en festival(es), incluyendo materiales para vendor internacional y distribuidor doméstico.</li>
                <li><strong>Independiente con campaña de impacto / distribución alternativa</strong> → además del festival y la distribución, reservas una <em>partida de consultoría inicial</em> (la campaña completa va en presupuesto aparte).</li>
              </ol>
              <Tip>
                Si los financiadores son broadcasters tradicionales, suelen <strong>no</strong> aceptar gastos
                de festival/distribución en el mismo presupuesto. En ese caso, prepara un presupuesto
                separado para esa fase y declara la «finish line» claramente.
              </Tip>

              <h3>14.3 Hipótesis del proyecto (Project Assumptions)</h3>
              <p>
                Toda decisión de presupuesto y calendario depende de unos supuestos clave. Antes de
                presupuestar, anota:
              </p>
              <ul>
                <li>Duración final estimada (minutos).</li>
                <li>Formato de rodaje y entrega (HD, 2K, 4K, HDR, idiomas).</li>
                <li>Localizaciones principales (país, ciudad, accesibilidad).</li>
                <li>Días de rodaje totales y rango temporal (¿se rueda en un mes o en 3 años?).</li>
                <li>Equipo clave ya identificado: dirección, producción, DOP, sonido, montaje.</li>
                <li>Cantidad estimada de archivo, animación, recreaciones.</li>
                <li>Periodo deseado de montaje, etalonaje, mezcla.</li>
                <li>Restricciones externas (efeméride, ventana de financiación, contrato firmado).</li>
                <li>Asunciones legales o de acceso conocidas (cesiones, derechos, conflictos).</li>
              </ul>
              <p>
                Si alguno de estos cambia <em>durante</em> el proyecto, el presupuesto y calendario
                deben revisarse. Una nota práctica: muchos proyectos parten con una asunción <em>sobre
                el propio nivel de presupuesto</em> (p. ej. «420K € comprometidos por TV3 + ICAA»). Esa es
                también una hipótesis de partida.
              </p>

              <h3>14.4 Plan A / Plan B / Plan C</h3>
              <p>
                Para enfrentar la incertidumbre de la financiación, Bahar propone trabajar con tres
                escenarios:
              </p>
              <ul>
                <li><strong>Plan A</strong> — Presupuesto óptimo con tarifas profesionales completas. Es el que enseñas a financiadores.</li>
                <li><strong>Plan B</strong> — Versión media: compromisos reales pero todavía digna. Uso interno.</li>
                <li><strong>Plan C</strong> — Mínimo viable: muchísimos favores, autofinanciación de los autores, recortes severos. Estricto uso interno; sólo se activa si A y B no se cumplen.</li>
              </ul>
              <Tip>
                En grabarte puedes hacer esto con la <strong>barra de Versiones</strong> del proyecto:
                duplica «Plan A» y rebaja partidas para crear «Plan B». Los exports respetan la versión
                activa, así que enseñas siempre el Plan A a los financiadores y mantienes B/C como
                respaldo privado.
              </Tip>

              <h3>14.5 Fases del proyecto</h3>
              <p>
                Aunque el ICAA pide el presupuesto agrupado en 12 capítulos, internamente conviene saber
                cuánto va a cada fase para planificar la tesorería:
              </p>
              <ol>
                <li><strong>Investigación y desarrollo</strong> (R&D): teaser, dossier, derechos.</li>
                <li><strong>Producción</strong>: rodaje, viajes, dietas, equipos, dirección/producción a media carga.</li>
                <li><strong>Montaje</strong>: editor, asistente, salas, primer corte → fine cut.</li>
                <li><strong>Postproducción</strong>: color, VFX, gráficos, mezcla, música.</li>
                <li><strong>Festivales, publicidad y distribución</strong>: DCPs, materiales, mercados.</li>
              </ol>
              <p>
                Una distribución típica (porcentajes muy aproximados; varían mucho según proyecto):
              </p>
              <ul>
                <li>R&D: ~12%</li>
                <li>Producción: ~32%</li>
                <li>Montaje: ~23%</li>
                <li>Postproducción: ~25%</li>
                <li>Festivales/publicidad/distribución: ~7%</li>
              </ul>

              <h3>14.6 Tabla de ratios profesionales (USA · IDA 2024)</h3>
              <p>Cifras orientativas que Bahar recoge tras décadas de práctica:</p>
              <ul>
                <li><strong>Director</strong>: 5-10% del presupuesto total. En España suele bajar al 4-7% por las tarifas locales.</li>
                <li><strong>Productor</strong>: 5-10% del presupuesto total.</li>
                <li><strong>Montaje</strong>: <strong>1 mes por cada 10 minutos</strong> de duración final (Alliance of Documentary Editors). Un largo de 90' → 9 meses de montaje.</li>
                <li><strong>Postproducción</strong>: 12 semanas de procesos + 4 semanas de entregables.</li>
                <li><strong>Color</strong>: entre 24h y 50h según ambición.</li>
                <li><strong>Mezcla final</strong>: entre 3 y 10 días.</li>
                <li><strong>Contingencia</strong>: 6-10% (en España, ICAA limita Cap.11 a 7%).</li>
                <li><strong>Honorario legal</strong>: 1-1,5% del presupuesto o flat fee con parámetros.</li>
                <li><strong>Fee production company</strong> (films comisionados): ~10% en lugar de royalties futuros.</li>
              </ul>

              <h3>14.7 Niveles de presupuesto en festivales y mercados</h3>
              <p>
                En el IDFA Forum 2023, los presupuestos pitch oscilaron así (datos públicos):
              </p>
              <ul>
                <li>Cluster mayoritario: <strong>200K-500K USD</strong>.</li>
                <li>Segundo cluster: 500K-700K.</li>
                <li>Tercer cluster: 800K-1,4M.</li>
                <li>Proyectos USA / Canadá / UK: media en torno a <strong>900K USD</strong>.</li>
                <li>Alto presupuesto (plataformas globales, estudio): 1,5M-3M y más.</li>
              </ul>
              <p>
                En España, presupuestos típicos ICAA Generales: 400K-1,2M €. Para creación / Cortometrajes:
                75K-250K €. (Verifica siempre la convocatoria vigente en la pestaña «Ayudas» de la app.)
              </p>

              <h3>14.8 In-kind, deferments y aportaciones no monetarias</h3>
              <ul>
                <li><strong>In-kind</strong>: bienes o servicios donados. Suelen ir en una columna aparte
                  (con un valor estimado) y se replican en el plan de financiación como «aportación en especie».
                  Atención: muchos financiadores sólo trabajan con el presupuesto en cash.</li>
                <li><strong>Deferments</strong>: honorarios (o parte de ellos) que se aplazan al éxito comercial
                  del proyecto. NO son ingreso. Deben quedar reflejados en contratos claros para evitar litigios.</li>
              </ul>

              <h3>14.9 Calendario (schedule) recomendado</h3>
              <p>
                El calendario en grabarte tiene barras por fase. Aquí los parámetros profesionales:
              </p>
              <ul>
                <li><strong>Research</strong>: tantos meses como necesite el sujeto. Si haces verité de largo plazo, multiplica.</li>
                <li><strong>Producción</strong>: concentra o disemina los rodajes según el ritmo del relato. Para
                  cinéma vérité, estima «cuántos días por mes vas a rodar» × meses (en lugar de planificar
                  cada día). Añade un buffer generoso.</li>
                <li><strong>Montaje</strong>: 1 mes por 10' (ver 14.6).</li>
                <li><strong>Postpro</strong>: planifica con cada vendor con meses de antelación.</li>
                <li><strong>Accesibilidad</strong>: deja semanas para transcripciones, CC/SDH, audio-descripción
                  y testeos con público diana.</li>
              </ul>
              <Tip>
                Cita de Bahar (de la Alliance of Documentary Editors): «A los editores rara vez se les
                invita a planificar el calendario del documental, pero el resultado de un calendario
                irreal se siente sobre todo en la sala de montaje.» Habla con tu montador/a antes de
                cerrar fechas.
              </Tip>

              <h3>14.10 Backup 3-2-1 del material rodado</h3>
              <p>
                Una regla a memorizar: cada brick de material debe tener al menos:
              </p>
              <ul>
                <li><strong>3</strong> copias digitales (idealmente con verificación de checksum).</li>
                <li><strong>2</strong> medios distintos (p. ej. disco + LTO).</li>
                <li><strong>1</strong> copia fuera del lugar de rodaje (off-site).</li>
              </ul>
              <p>
                Esto se presupuesta en el Cap.08 (Soportes digitales). El coste es ridículo comparado con
                el riesgo de perder material irrepetible.
              </p>

              <h3>14.11 Accesibilidad (nueva en 2025)</h3>
              <p>
                Bahar dedica una sección entera a accesibilidad. Los entregables «accesibles» son ya
                obligatorios en muchas plataformas, festivales y premios:
              </p>
              <ul>
                <li><strong>Transcripciones</strong> de todo el audio y elementos visuales.</li>
                <li><strong>Subtítulos para sordos / hipoacúsicos (SDH)</strong> con descripciones de música y efectos.</li>
                <li><strong>Audio-descripción (AD)</strong> para personas ciegas o con baja visión.</li>
                <li><strong>Intérprete LSE quemado en pantalla</strong> (opcional, menos común).</li>
                <li><strong>Production access coordinator</strong>: figura emergente; un par de días en cada fase del proceso.</li>
                <li>Reservar ~<strong>2% del presupuesto</strong> en la fase inicial para accesibilidad del equipo (riders confidenciales).</li>
              </ul>
              <Tip>
                Pide a tu equipo y participantes un «access rider» voluntario para identificar
                necesidades específicas (no sólo discapacidad: también cuidado de mayores, jornada
                compatible con conciliación, etc.).
              </Tip>

              <h3>14.12 Errores clásicos a evitar</h3>
              <ol>
                <li><strong>Trabajar con «allows»</strong> (estimaciones vagas) en vez de partidas detalladas: en
                  cuanto un financiador pregunta «¿por qué 1000 € de oficina?», no sabes qué responder.
                  Mejor: «10 meses × 100 €/mes oficina = 1000 €».</li>
                <li><strong>No reservar contingencia</strong>: el rodaje siempre sorprende. 6-10% en Cap.11 te
                  salva de perder el control.</li>
                <li><strong>Dejar «fix it in post»</strong>: post es la segunda mitad del maratón. No puedes
                  llegar agotado y con mochila pesada. Resuelve en rodaje.</li>
                <li><strong>Subestimar deliverables</strong>: el máster final, los DCPs, los entregables
                  multidioma. Esa partida del Cap.09 es la que más se infla en última hora.</li>
                <li><strong>Confundir personal contratado y autónomo</strong>: en España hay implicaciones
                  fiscales y laborales serias. Consulta con gestoría.</li>
                <li><strong>No hablar con el editor antes de calendarizar</strong>: ver 14.9.</li>
              </ol>

              <h3>14.13 Plan de financiación: cómo presentarlo</h3>
              <p>
                Junto al presupuesto, presenta una tabla de financiación con dos secciones:
              </p>
              <ul>
                <li><strong>Confirmado</strong>: contratos firmados, ayudas concedidas.</li>
                <li><strong>Pendiente</strong>: solicitudes en curso, conversaciones avanzadas.</li>
              </ul>
              <p>
                Marca qué financiadores adquieren derechos de distribución y cuáles participan en el
                «waterfall» de royalties. El cash invertido por la productora debería tener prioridad
                de recuperación («first position recoupment») cuando llegue el ingreso.
              </p>

              <h3>14.14 Recursos externos recomendados</h3>
              <ul>
                <li><strong>Artículo original Bahar 2025 (IDA)</strong>: <a className="text-[#26775E] underline" href="https://www.documentary.org/online-feature/2025-introduction-documentary-budgeting-and-scheduling" target="_blank" rel="noreferrer">documentary.org</a> · incluye plantilla Excel de presupuesto y calendario.</li>
                <li><strong>Doc Society — Impact Field Guide & Toolkit</strong>: <a className="text-[#26775E] underline" href="https://impactguide.org/" target="_blank" rel="noreferrer">impactguide.org</a></li>
                <li><strong>Documentary Producers Alliance — Waterfall Guidelines</strong>: <a className="text-[#26775E] underline" href="https://www.docproducers.org/our-work" target="_blank" rel="noreferrer">docproducers.org</a></li>
                <li><strong>Alliance of Documentary Editors — Schedule Guide</strong>: <a className="text-[#26775E] underline" href="https://allianceofdoceditors.com/about/ade_scheduling_guide/" target="_blank" rel="noreferrer">allianceofdoceditors.com</a></li>
                <li><strong>FWD-Doc — Accessibility resources</strong>: <a className="text-[#26775E] underline" href="https://www.fwd-doc.org/resources" target="_blank" rel="noreferrer">fwd-doc.org</a></li>
                <li><strong>IDA Nonfiction Access Initiative</strong>: <a className="text-[#26775E] underline" href="https://www.documentary.org/nonfiction-access-initiative/resources" target="_blank" rel="noreferrer">documentary.org</a></li>
              </ul>

              <h3>14.15 Glosario rápido</h3>
              <dl className="mt-2 space-y-2">
                <div><dt className="inline font-semibold">ATL / BTL</dt><dd className="inline"> · Above-the-Line / Below-the-Line: en presupuestos anglosajones, dirección + producción + guión + reparto van «sobre la línea»; el resto, «bajo la línea». En ICAA no es estricto pero se respeta como ordenación mental.</dd></div>
                <div><dt className="inline font-semibold">Flat fee</dt><dd className="inline"> · Honorario plano contractual (no por horas). Requiere parámetros claros: duración del proyecto, alcance, revisiones, qué pasa si se extiende.</dd></div>
                <div><dt className="inline font-semibold">Allow / Allowance</dt><dd className="inline"> · Estimación global (p.ej. «1000 € allow para material oficina»). Mejor evitarla — detalla siempre por unidades.</dd></div>
                <div><dt className="inline font-semibold">R/T</dt><dd className="inline"> · Roundtrip — billete ida y vuelta.</dd></div>
                <div><dt className="inline font-semibold">In-kind</dt><dd className="inline"> · Contribución no monetaria (bienes o servicios donados).</dd></div>
                <div><dt className="inline font-semibold">Deferment</dt><dd className="inline"> · Honorario aplazado, cobrable si el proyecto genera beneficios.</dd></div>
                <div><dt className="inline font-semibold">Contingency</dt><dd className="inline"> · Partida de imprevistos (típicamente 6-10%). En España, Cap.11 ≤ 7%.</dd></div>
                <div><dt className="inline font-semibold">Fiscal sponsor</dt><dd className="inline"> · ONG estadounidense que recibe donaciones deducibles en nombre del proyecto. En España, su equivalente serían entidades culturales con personalidad jurídica.</dd></div>
                <div><dt className="inline font-semibold">E&O Insurance</dt><dd className="inline"> · Errors & Omissions: seguro de responsabilidad para distribución (derechos de imagen, fair use, difamación). Requerido por casi todos los distribuidores y plataformas.</dd></div>
                <div><dt className="inline font-semibold">DCP</dt><dd className="inline"> · Digital Cinema Package: copia digital para proyección en sala. La «accesible» lleva subtítulos y AD quemados.</dd></div>
                <div><dt className="inline font-semibold">3-2-1 backup</dt><dd className="inline"> · 3 copias / 2 medios distintos / 1 off-site. Estándar de la industria para no perder material.</dd></div>
                <div><dt className="inline font-semibold">Waterfall</dt><dd className="inline"> · Orden en que los ingresos del proyecto se reparten entre financiadores, productora, autores y deferments.</dd></div>
                <div><dt className="inline font-semibold">Finish line</dt><dd className="inline"> · Hasta dónde llega lo que cubres en este presupuesto (ver 14.2).</dd></div>
              </dl>

              <p className="mt-6 text-[12px] italic text-stone-500">
                © Robert Bahar / International Documentary Association 2024. Resumen y adaptación al
                contexto español por el equipo de grabarte. Cualquier divergencia con el original es
                responsabilidad de esta adaptación, no del autor.
              </p>
            </Section>

            <Section id="faq" title="15. Preguntas frecuentes">
              <h3>¿Pueden colaborar varios miembros del equipo a la vez sobre el mismo proyecto?</h3>
              <p>
                Sí — los datos viven en el servidor. Si dos personas editan a la vez la misma sección, gana la
                última en guardar. Para evitar pisarse, repartid responsabilidades por sección.
              </p>

              <h3>¿Mis textos están seguros?</h3>
              <p>
                Sí. Todo se guarda en la base de datos de Grabarte 360 dentro de la infraestructura de Emergent.
                El acceso está cerrado por Google Login + lista blanca.
              </p>

              <h3>¿Qué pasa si la IA dice cosas que no son ciertas?</h3>
              <p>
                Los textos generados son <strong>borradores</strong>. Siempre debes revisarlos. La IA tiene instrucciones
                explícitas de no inventar hechos, pero ningún modelo es infalible. Trabaja como editor de tu copiloto.
              </p>

              <h3>¿Puedo deshacer un cambio?</h3>
              <p>
                No hay <em>undo</em> global de texto. Sin embargo, en el chat IA siempre tienes el botón
                <em> Añadir al final</em> en lugar de <em>Reemplazar</em>, así no pierdes lo anterior. También puedes
                copiar la propuesta antes de aplicar.
              </p>

              <h3>¿Cuánto cuesta usar la IA?</h3>
              <p>
                La Clave Universal Emergent se factura por tokens. Cada generación de texto consume entre $0,01 y
                $0,10 dependiendo del tamaño. Las imágenes y las transcripciones tienen su propio precio.
              </p>

              <h3>¿Por qué a veces tarda?</h3>
              <p>
                Las tareas largas (importar texto en guion, derivar formato, generar varias imágenes) implican
                varias llamadas a la IA en paralelo. Te mostramos un indicador "Pensando…" en cada caso. Espera
                pacientemente; ningún proceso debe pasar de 3 minutos.
              </p>
            </Section>

            <p className="mt-12 border-t border-stone-300/70 pt-6 text-xs italic text-stone-500">
              Si echas en falta algo en este manual, dilo en el siguiente brief de mejoras y se añade.
            </p>
          </article>
        </div>
      </div>
    </div>
  );
}

const Section = ({ id, title, children }) => (
  <section id={id} className="mb-14 scroll-mt-24" data-testid={`section-${id}`}>
    <h2 className="font-display text-2xl tracking-tight text-stone-900 sm:text-3xl">{title}</h2>
    <div className="mt-4 space-y-3 font-serif text-[15px] leading-relaxed text-stone-800">
      {children}
    </div>
  </section>
);

const Tip = ({ children }) => (
  <aside className="my-3 rounded-lg border-l-4 border-[#26775E] bg-[#26775E]/5 px-4 py-3 text-[13px] text-stone-700">
    <span className="mr-2 inline-block rounded-full bg-[#26775E] px-2 py-0.5 text-[9px] font-semibold uppercase tracking-widest text-white">Tip</span>
    {children}
  </aside>
);
